import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { useRepresentative } from '../config/RepresentativeContext';
import { AchievementsBadges, PollPointsCard } from '../components/AchievementsSection';
import SpotlightPollCard from '../components/SpotlightPollCard';
import RelatedNews from '../components/RelatedNews';
import { MdAccountBalance, MdHowToVote, MdTrendingUp, MdArrowForward, MdNewReleases, MdChatBubble, MdVisibility, MdGavel, MdNotificationsActive, MdPushPin, MdCheckCircle } from 'react-icons/md';
import { FaSeedling, FaUserGraduate, FaHandsHelping, FaFireAlt, FaCrown, FaChessKing, FaTrophy, FaMedal, FaGem, FaStar, FaBolt, FaRocket, FaShieldAlt } from 'react-icons/fa';
import { toast } from 'sonner';

const ICON_MAP = {
  seedling: FaSeedling, graduate: FaUserGraduate, hands: FaHandsHelping,
  fire: FaFireAlt, medal: FaMedal, trophy: FaTrophy, crown: FaCrown,
  king: FaChessKing, gem: FaGem, star: FaStar, bolt: FaBolt,
  rocket: FaRocket, shield: FaShieldAlt,
};

const FALLBACK_TIERS = [
  { min: 0, max: 0, title: 'Newcomer', icon: 'seedling', color: '#9CA3AF' },
  { min: 1, max: 24, title: 'Beginner', icon: 'graduate', color: '#6B7280' },
  { min: 25, max: 74, title: 'Contributor', icon: 'hands', color: '#10B981' },
  { min: 75, max: 149, title: 'Enthusiast', icon: 'fire', color: '#F59E0B' },
  { min: 150, max: 299, title: 'Poll Pro', icon: 'medal', color: '#3B82F6' },
  { min: 300, max: 499, title: 'Poll Master', icon: 'trophy', color: '#8B5CF6' },
  { min: 500, max: 999, title: 'Poll Champion', icon: 'crown', color: '#EC4899' },
  { min: 1000, max: 2499, title: 'Poll Legend', icon: 'king', color: '#EF4444' },
  { min: 2500, max: null, title: 'Poll Titan', icon: 'gem', color: '#D946EF' },
];

function getTierForPoints(tiers, points) {
  const sorted = [...tiers].sort((a, b) => b.min - a.min);
  return sorted.find(t => points >= t.min) || tiers[0];
}

function getNextTierForPoints(tiers, points) {
  const sorted = [...tiers].sort((a, b) => a.min - b.min);
  return sorted.find(t => t.min > points) || null;
}


function HomePage() {
  const [stats, setStats] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [ratingTiers, setRatingTiers] = useState(null);
  const [watchedBills, setWatchedBills] = useState([]);
  const [billUnreadCount, setBillUnreadCount] = useState(0);
  const [topContestedBills, setTopContestedBills] = useState([]);
  const [trendingTags, setTrendingTags] = useState([]);
  const [pinningTagId, setPinningTagId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleTagClick = (tagId) => {
    navigate(`/polls?tag=${tagId}`);
  };

  const handlePinTrendingTag = async (tagId, tagName) => {
    setPinningTagId(tagId);
    try {
      await axios.post(`${API_URL}/api/favorites/tags`, { tag_id: tagId });
      // Optimistic update: mark this tag as pinned so the chip flips state
      setTrendingTags(prev => prev.map(t => t.tag_id === tagId ? { ...t, is_pinned_by_me: true } : t));
      toast.success(`Pinned "${tagName}" to your interests`);
    } catch (err) {
      const msg = err.response?.data?.detail || '';
      toast.error(msg.includes('user_favorite_tags') ? 'Tags storage not yet initialized — apply the migration in Supabase.' : 'Failed to pin tag');
    } finally {
      setPinningTagId(null);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // Load stats, achievements, tiers, featured polls, watched bills, and unread bill notifications in parallel
      const [statsResponse, achievementsResponse, featuredResponse, tiersResponse, billsResponse, billNotifResponse, topContestedResponse, trendingTagsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/user/stats`),
        axios.get(`${API_URL}/api/user/achievements`),
        axios.get(`${API_URL}/api/polls/featured`),
        axios.get(`${API_URL}/api/poll-rating-tiers`).catch(() => ({ data: { tiers: null } })),
        axios.get(`${API_URL}/api/bills/me/subscriptions`).catch(() => ({ data: { subscriptions: [] } })),
        axios.get(`${API_URL}/api/notifications`, {
          params: { notification_type: 'bill_status_change', unread_only: true, limit: 1 },
        }).catch(() => ({ data: { unread_count: 0 } })),
        axios.get(`${API_URL}/api/bills/me/top-contested`, {
          params: { limit: 3, min_votes: 2 },
        }).catch(() => ({ data: { bills: [] } })),
        axios.get(`${API_URL}/api/favorites/tags/trending`, {
          params: { days: 7, limit: 6 },
        }).catch(() => ({ data: { items: [] } })),
      ]);
      setStats(statsResponse.data);
      setAchievements(achievementsResponse.data);
      setFeatured(featuredResponse.data);
      setRatingTiers(tiersResponse.data?.tiers);
      setWatchedBills(billsResponse.data?.subscriptions || []);
      setBillUnreadCount(billNotifResponse.data?.unread_count || 0);
      setTopContestedBills(topContestedResponse.data?.bills || []);
      setTrendingTags(trendingTagsResponse.data?.items || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getGroupedCategories = () => {
    if (!stats?.tagBreakdown) return [];
    
    const grouped = {};
    
    stats.tagBreakdown.forEach(tag => {
      const catName = tag.parentName || 'Uncategorized';
      if (!grouped[catName]) {
        grouped[catName] = {
          name: catName,
          color: tag.parentColor || '#6B7280',
          tags: [],
          totalCount: 0,
        };
      }
      grouped[catName].tags.push(tag);
      grouped[catName].totalCount += tag.totalCount;
    });

    return Object.values(grouped).sort((a, b) => b.totalCount - a.totalCount).slice(0, 6);
  };

  const topCategories = getGroupedCategories();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Spotlight Poll */}
        <SpotlightPollCard />

        {/* Stats Overview — 2 consolidated cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Card 1: Role + Poll Rating */}
          <RolePollRatingCard
            totalPoints={achievements?.pollPoints?.total ?? 0}
            tiers={ratingTiers}
          />
          {/* Card 2: Poll Progress (Answered + Remaining) */}
          <PollProgressCard
            answered={stats?.answeredPolls || 0}
            total={stats?.totalPolls || 0}
            navigate={navigate}
          />
        </div>

        {/* Achievements & Milestones Section */}
        {achievements && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <PollPointsCard 
              pollPoints={achievements.pollPoints} 
              stats={achievements.stats}
            />
            <AchievementsBadges badges={achievements.badges} />
          </div>
        )}

        {/* My Watchlist (bills the user is following) */}
        {watchedBills && watchedBills.length > 0 && (
          <BillsWatchlistCard bills={watchedBills} navigate={navigate} unreadCount={billUnreadCount} />
        )}

        {/* Top contested bills near the user (engagement + close split) */}
        {topContestedBills && topContestedBills.length > 0 && (
          <TopContestedBillsCard bills={topContestedBills} navigate={navigate} />
        )}

        {/* "In the news" — civic news headlines for the user's state/country.
            Wrapper is rendered inside RelatedNews so the whole card disappears
            when Google News returns nothing for this location. */}
        <RelatedNews
          endpoint="/api/news/homepage"
          limit={5}
          title="In the news"
          testIdPrefix="homepage-news"
          hideWhenEmpty
          wrapperClassName="card-container mb-6"
        />

        {/* New and Trending Polls */}
        <div className="flex items-center gap-3 mb-6 px-1">
          <MdTrendingUp className="text-2xl text-primary" />
          <h3 className="text-2xl font-bold text-white font-rubik tracking-tight">New and Trending Polls</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Polls by Responses */}
          <div className="card-container">
            <div className="flex items-center gap-2 mb-4">
              <MdTrendingUp className="text-xl text-primary" />
              <h3 className="text-lg font-semibold text-gray-800 font-rubik">Most Popular Polls</h3>
            </div>
            {featured?.topPopular?.length > 0 ? (
              <div className="space-y-3">
                {featured.topPopular.map((poll, i) => (
                  <button
                    key={poll.pollId}
                    onClick={() => navigate(`/polls/${poll.pollId}`)}
                    className="w-full flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
                    data-testid={`popular-poll-${i}`}
                  >
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary font-bold text-sm flex items-center justify-center font-rubik">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 font-rubik line-clamp-2 group-hover:text-primary transition-colors">
                        {poll.question}
                      </p>
                      <div className="flex items-center gap-1 mt-1.5">
                        <MdChatBubble className="text-xs text-gray-400" />
                        <span className="text-xs text-gray-500 font-rubik">{poll.responseCount} responses</span>
                      </div>
                    </div>
                    <MdArrowForward className="flex-shrink-0 text-gray-300 group-hover:text-primary transition-colors mt-1" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-gray-400 text-sm font-rubik">No polls yet</p>
            )}
          </div>

          {/* Newest Polls */}
          <div className="card-container">
            <div className="flex items-center gap-2 mb-4">
              <MdNewReleases className="text-xl text-secondary" />
              <h3 className="text-lg font-semibold text-gray-800 font-rubik">Newest Polls</h3>
            </div>
            {featured?.topNewest?.length > 0 ? (
              <div className="space-y-3">
                {featured.topNewest.map((poll, i) => (
                  <button
                    key={poll.pollId}
                    onClick={() => navigate(`/polls/${poll.pollId}`)}
                    className="w-full flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-secondary/40 hover:bg-secondary/5 transition-all text-left group"
                    data-testid={`newest-poll-${i}`}
                  >
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-secondary/10 text-secondary font-bold text-sm flex items-center justify-center font-rubik">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 font-rubik line-clamp-2 group-hover:text-secondary transition-colors">
                        {poll.question}
                      </p>
                      <span className="text-xs text-gray-500 font-rubik mt-1.5 block">
                        {new Date(poll.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <MdArrowForward className="flex-shrink-0 text-gray-300 group-hover:text-secondary transition-colors mt-1" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-gray-400 text-sm font-rubik">No polls yet</p>
            )}
          </div>
        </div>

        {/* Trending Interests — platform pin-leaders + your activity by category */}
        <div className="mb-8" data-testid="trending-interests-section">
          <div className="flex items-center gap-3 mb-6 px-1">
            <FaFireAlt className="text-2xl text-primary" />
            <h3 className="text-2xl font-bold text-white font-rubik tracking-tight">Trending Interests</h3>
          </div>

          {/* Platform-wide most-pinned tags in the last 7 days */}
          {trendingTags && trendingTags.length > 0 && (
            <div className="card-container mb-6 bg-gradient-to-br from-amber-50 to-rose-50 border-amber-200" data-testid="trending-interests-tags">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-sm font-bold text-amber-900 font-rubik uppercase tracking-wide flex items-center gap-2">
                    <FaFireAlt className="text-amber-600" /> Trending across CIVOTE
                  </h4>
                  <p className="text-xs text-amber-700/80 font-rubik mt-0.5">
                    Most-pinned topics in the last 7 days — pin one to personalize your feed.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/favorites?tab=tags')}
                  className="text-xs font-semibold text-primary hover:underline font-rubik"
                  data-testid="trending-interests-manage-link"
                >
                  Manage interests →
                </button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="trending-interests-chips">
                {trendingTags.map(t => (
                  <span
                    key={t.tag_id}
                    className={`inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full text-xs font-rubik border transition-colors ${
                      t.is_pinned_by_me
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                        : 'bg-white border-amber-300 text-amber-900 hover:bg-amber-100'
                    }`}
                    data-testid={`trending-interest-chip-${t.tag_id}`}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: t.color }} />
                    <button
                      type="button"
                      onClick={() => handleTagClick(t.tag_id)}
                      className="font-medium hover:underline"
                      title={`See polls tagged ${t.name}`}
                    >
                      {t.name}
                    </button>
                    <span className="text-[10px] text-gray-500 px-1.5 py-0.5 rounded-full bg-white/60 border border-gray-200">
                      {t.pin_count} pin{t.pin_count === 1 ? '' : 's'}
                    </span>
                    {t.is_pinned_by_me ? (
                      <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold uppercase tracking-wide">
                        <MdCheckCircle className="text-[12px]" /> Pinned
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handlePinTrendingTag(t.tag_id, t.name)}
                        disabled={pinningTagId === t.tag_id}
                        className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 text-[10px] font-semibold uppercase tracking-wide disabled:opacity-50"
                        data-testid={`trending-interest-pin-${t.tag_id}`}
                      >
                        <MdPushPin className="text-[12px]" /> Pin
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Your activity by category — personal poll progress per category */}
          {topCategories && topCategories.length > 0 ? (
            <>
              <h4 className="text-sm font-bold text-white/80 font-rubik uppercase tracking-wide mb-3 px-1 flex items-center gap-2">
                <MdTrendingUp className="text-secondary" /> Your activity by category
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {topCategories.map((category, idx) => (
                  <div 
                    key={category.name} 
                    className="card-container flex flex-col h-full hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 border-t-4 bg-white"
                    style={{ borderTopColor: category.color }}
                    data-testid={`category-card-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-5">
                      <h4 className="text-lg font-bold font-rubik truncate" style={{ color: category.color }}>
                        {category.name}
                      </h4>
                      <span className="text-xs font-bold bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-rubik border border-gray-200">
                        {category.totalCount} Polls
                      </span>
                    </div>
                    
                    <div className="flex flex-col gap-3 flex-1">
                      {category.tags.slice(0, 4).map(tag => {
                        const progress = Math.min(100, Math.round((tag.answeredCount / (tag.totalCount || 1)) * 100));
                        return (
                          <button
                            key={tag.tagId}
                            onClick={() => handleTagClick(tag.tagId)}
                            className="group flex flex-col p-3 rounded-lg border border-gray-100 bg-gray-50 hover:bg-white hover:border-primary hover:shadow-sm transition-all duration-200 text-left w-full"
                            data-testid={`dashboard-tag-${tag.tagId}`}
                          >
                            <div className="flex items-center justify-between w-full mb-2">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }}></span>
                                <span className="font-semibold text-gray-700 font-rubik text-sm truncate group-hover:text-primary transition-colors">
                                  {tag.name}
                                </span>
                              </div>
                              <span className="text-xs font-bold text-gray-500 font-rubik flex-shrink-0 ml-2">
                                {tag.totalCount}
                              </span>
                            </div>
                            
                            <div className="w-full flex items-center gap-3">
                              <div className="h-1.5 flex-1 bg-gray-200 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-500 bg-primary/80 group-hover:bg-primary" style={{ width: `${progress}%` }} />
                              </div>
                              <span className="text-[10px] text-gray-400 font-rubik whitespace-nowrap font-medium">
                                {tag.answeredCount} ans
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    
                    {category.tags.length > 4 && (
                      <div className="mt-4 pt-3 border-t border-gray-100 text-center">
                        <span className="text-xs text-gray-400 font-semibold font-rubik bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
                          +{category.tags.length - 4} more tags
                      </span>
                    </div>
                  )}
                </div>
              ))}
              </div>
            </>
          ) : (
            <div className="card-container text-center py-12">
              <p className="text-gray-500 font-rubik font-medium">No poll categories available</p>
            </div>
          )}
        </div>
      </div>
  );
}

function BillsWatchlistCard({ bills, navigate, unreadCount = 0 }) {
  // Show up to 3 most recently subscribed bills
  const top = (bills || [])
    .slice()
    .sort((a, b) => {
      const at = new Date(a.updated_at || a.created_at || 0).getTime();
      const bt = new Date(b.updated_at || b.created_at || 0).getTime();
      return bt - at;
    })
    .slice(0, 3);

  const billsHref = unreadCount > 0 ? '/bills?updates=1' : '/bills';
  const goBills = () => navigate(billsHref);

  const SCOPE_LABEL = { local: 'Local', state: 'State', national: 'National' };
  const SCOPE_COLOR = {
    local: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    state: 'text-sky-700 bg-sky-50 border-sky-200',
    national: 'text-rose-700 bg-rose-50 border-rose-200',
  };

  return (
    <div className="card-container mb-8" data-testid="watchlist-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="relative inline-flex">
            <MdNotificationsActive className="text-xl text-primary" />
            {unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold font-rubik leading-none ring-2 ring-white"
                data-testid="watchlist-unread-dot"
                aria-label={`${unreadCount} new bill status update${unreadCount === 1 ? '' : 's'}`}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </span>
          <h3 className="text-lg font-semibold text-gray-800 font-rubik">My Bill Watchlist</h3>
          <span className="text-xs text-gray-500 ml-1">({bills.length})</span>
          {unreadCount > 0 && (
            <span
              className="ml-1 text-xs font-rubik font-semibold text-red-600 hidden sm:inline"
              data-testid="watchlist-unread-label"
            >
              {unreadCount} new update{unreadCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={goBills}
          className="text-sm font-rubik font-medium text-primary hover:text-primary/80 inline-flex items-center gap-1"
          data-testid="watchlist-view-all"
        >
          View all <MdArrowForward className="text-base" />
        </button>
      </div>
      <div className="space-y-3">
        {top.map((b) => (
          <button
            key={b.id}
            type="button"
            onClick={goBills}
            className="w-full flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
            data-testid={`watchlist-bill-${b.id}`}
          >
            <div className="mt-0.5 text-primary">
              <MdGavel className="text-xl" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 font-rubik line-clamp-2 group-hover:text-primary transition-colors">
                {b.bill_name}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                <span className={`px-2 py-0.5 rounded-full border font-rubik ${SCOPE_COLOR[b.scope] || 'text-gray-600 bg-gray-50 border-gray-200'}`}>
                  {SCOPE_LABEL[b.scope] || b.scope}
                </span>
                {b.bill_id && (
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-mono">
                    {b.bill_id}
                  </span>
                )}
                {b.last_known_status && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    {b.last_known_status}
                  </span>
                )}
              </div>
            </div>
            <MdArrowForward className="flex-shrink-0 text-gray-300 group-hover:text-primary transition-colors mt-1" />
          </button>
        ))}
      </div>
      {bills.length > 3 && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-center">
          <span className="text-xs text-gray-400 font-semibold font-rubik bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
            +{bills.length - 3} more being watched
          </span>
        </div>
      )}
    </div>
  );
}

function TopContestedBillsCard({ bills, navigate }) {
  const SCOPE_LABEL = { local: 'Local', state: 'State', national: 'National' };
  const SCOPE_COLOR = {
    local: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    state: 'text-sky-700 bg-sky-50 border-sky-200',
    national: 'text-rose-700 bg-rose-50 border-rose-200',
  };

  return (
    <div className="card-container mb-8" data-testid="top-contested-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MdTrendingUp className="text-xl text-primary" />
          <h3 className="text-lg font-semibold text-gray-800 font-rubik">Top Contested Bills Near You</h3>
        </div>
        <button
          type="button"
          onClick={() => navigate('/bills')}
          className="text-sm font-rubik font-medium text-primary hover:text-primary/80 inline-flex items-center gap-1"
          data-testid="top-contested-view-all"
        >
          View all <MdArrowForward className="text-base" />
        </button>
      </div>
      <div className="space-y-3">
        {bills.map((b) => {
          const total = b.total || 0;
          const yesPct = total > 0 ? Math.round((b.yes / total) * 100) : 0;
          const noPct = total > 0 ? 100 - yesPct : 0;
          return (
            <button
              key={b.bill_key}
              type="button"
              onClick={() => navigate(`/bills?focus=${encodeURIComponent(b.bill_key)}`)}
              className="w-full flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
              data-testid={`top-contested-bill-${b.bill_key}`}
            >
              <div className="mt-0.5 text-primary">
                <MdGavel className="text-xl" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 font-rubik line-clamp-2 group-hover:text-primary transition-colors">
                  {b.bill_name}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full border font-rubik ${SCOPE_COLOR[b.scope] || 'text-gray-600 bg-gray-50 border-gray-200'}`}>
                    {SCOPE_LABEL[b.scope] || b.scope}
                  </span>
                  {b.bill_id && (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-mono">
                      {b.bill_id}
                    </span>
                  )}
                  <span className="text-gray-500 tabular-nums">
                    {total} {total === 1 ? 'vote' : 'votes'}
                  </span>
                  {b.my_vote && (
                    <span className={`px-2 py-0.5 rounded-full border font-rubik font-semibold ${
                      b.my_vote === 'yes'
                        ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                        : 'text-rose-700 bg-rose-50 border-rose-200'
                    }`}>
                      You voted {b.my_vote === 'yes' ? 'Yes' : 'No'}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden flex">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${yesPct}%` }} />
                    <div className="h-full bg-rose-500 transition-all" style={{ width: `${noPct}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 font-rubik tabular-nums shrink-0">
                    {yesPct}% / {noPct}%
                  </span>
                </div>
              </div>
              <MdArrowForward className="flex-shrink-0 text-gray-300 group-hover:text-primary transition-colors mt-1" />
            </button>
          );
        })}
      </div>
    </div>
  );
}




function RolePollRatingCard({ totalPoints, tiers }) {
  const { isRepresentative, repProfileId } = useRepresentative();
  const navigate = useNavigate();
  const tierList = tiers && tiers.length > 0 ? tiers : FALLBACK_TIERS;
  const tier = getTierForPoints(tierList, totalPoints);
  const nextTier = getNextTierForPoints(tierList, totalPoints);
  const IconComp = ICON_MAP[tier?.icon] || FaStar;
  const progress = nextTier ? Math.round(((totalPoints - tier.min) / (nextTier.min - tier.min)) * 100) : 100;
  const tierColor = tier?.color || '#6B7280';
  const roleColor = isRepresentative ? '#08B23C' : '#00C4CC';

  return (
    <div className="card-container flex items-stretch gap-0 p-0 overflow-hidden" data-testid="role-rating-card">
      {/* Left: Role — colored panel */}
      <div
        className="flex flex-col items-center justify-center px-5 py-4 min-w-[120px] sm:min-w-[140px]"
        style={{ background: `linear-gradient(135deg, ${roleColor}20, ${roleColor}10)` }}
      >
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-2" style={{ backgroundColor: `${roleColor}25` }}>
          {isRepresentative ? (
            <MdAccountBalance className="text-2xl" style={{ color: roleColor }} />
          ) : (
            <MdHowToVote className="text-2xl" style={{ color: roleColor }} />
          )}
        </div>
        <div className="text-base font-bold font-rubik text-center" style={{ color: roleColor }}>
          {isRepresentative ? 'Representative' : 'Voter'}
        </div>
        {isRepresentative && repProfileId && (
          <button
            onClick={() => navigate(`/representatives/${repProfileId}`)}
            className="flex items-center gap-1 text-xs font-medium mt-1.5 transition-colors"
            style={{ color: roleColor }}
            data-testid="view-public-profile-btn"
          >
            <MdVisibility className="text-xs" /> Profile
          </button>
        )}
      </div>
      {/* Right: Poll Rating */}
      <div className="flex-1 px-5 py-4 flex flex-col justify-center">
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide font-rubik mb-2">Poll Rating</div>
        <div className="flex items-center gap-2 mb-2">
          <IconComp className="text-xl flex-shrink-0" style={{ color: tierColor }} />
          <span className="text-lg font-bold font-rubik truncate" style={{ color: tierColor }} data-testid="poll-rating-title">
            {tier?.title || 'Newcomer'}
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: tierColor }} />
        </div>
        {nextTier ? (
          <div className="text-xs text-gray-500 font-rubik">{totalPoints}/{nextTier.min} to <span className="font-semibold">{nextTier.title}</span></div>
        ) : (
          <div className="text-xs font-rubik" style={{ color: tierColor }}>Max rank achieved!</div>
        )}
      </div>
    </div>
  );
}

function PollProgressCard({ answered, total, navigate }) {
  const remaining = total - answered;
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
  const circumference = 2 * Math.PI * 36;
  const strokeDashoffset = circumference - (pct / 100) * circumference;

  return (
    <div className="card-container flex items-stretch gap-0 p-0 overflow-hidden" data-testid="poll-progress-card">
      {/* Left: Progress Ring panel */}
      <div className="flex flex-col items-center justify-center px-5 py-4 min-w-[120px] sm:min-w-[140px] bg-gradient-to-b from-primary/10 to-primary/5">
        <div className="relative" data-testid="progress-ring">
          <svg width="80" height="80" viewBox="0 0 88 88">
            <circle cx="44" cy="44" r="36" fill="none" stroke="#e5e7eb" strokeWidth="6" />
            <circle
              cx="44" cy="44" r="36" fill="none"
              stroke="#08B23C" strokeWidth="6" strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 44 44)"
              className="transition-all duration-700"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold text-gray-800 font-rubik">{pct}%</span>
          </div>
        </div>
        <div className="text-xs text-gray-500 font-rubik mt-1">{total} polls</div>
      </div>
      {/* Right: Answered + Remaining side by side */}
      <div className="flex-1 flex items-center justify-center gap-8 px-5 py-4">
        <button
          onClick={() => navigate('/polls?filter=answered')}
          className="flex flex-col items-center group cursor-pointer"
          data-testid="answered-polls-home-card"
        >
          <div className="text-sm font-medium text-gray-400 uppercase tracking-wide font-rubik mb-1">Answered</div>
          <div className="text-5xl font-bold text-primary font-rubik group-hover:scale-110 transition-transform">{answered}</div>
        </button>
        <div className="w-px h-16 bg-gray-200" />
        <button
          onClick={() => navigate('/polls?filter=unanswered')}
          className="flex flex-col items-center group cursor-pointer"
          data-testid="remaining-polls-home-card"
        >
          <div className="text-sm font-medium text-gray-400 uppercase tracking-wide font-rubik mb-1">Remaining</div>
          <div className="text-5xl font-bold text-secondary font-rubik group-hover:scale-110 transition-transform">{remaining}</div>
        </button>
      </div>
    </div>
  );
}

export default HomePage;
